import React, { useEffect, useRef } from "react";
import { Chart, registerables } from "chart.js";

Chart.register(...registerables);

interface PerformanceRadarProps {
  processGroups: Record<string, number>;
}

export default function PerformanceRadar({ processGroups }: PerformanceRadarProps) {
  const chartRef = useRef<HTMLCanvasElement>(null);
  const chartInstanceRef = useRef<Chart | null>(null);

  useEffect(() => {
    const ctx = chartRef.current?.getContext("2d");
    const labels = Object.keys(processGroups);
    const currentValues = labels.map((k) => processGroups[k]);
    const targetValues = new Array(labels.length).fill(4.5);

    if (!chartInstanceRef.current && ctx) {
      chartInstanceRef.current = new Chart(ctx, {
        type: "radar",
        data: {
          labels,
          datasets: [
            {
              label: "Current State",
              data: currentValues,
              backgroundColor: "rgba(76, 175, 80, 0.2)",
              borderColor: "rgba(46, 125, 50, 1)",
              borderWidth: 3,
              pointBackgroundColor: "rgba(46, 125, 50, 1)",
              pointBorderColor: "#fff",
              pointBorderWidth: 2,
              pointRadius: 6,
              pointHoverRadius: 8,
            },
            {
              label: "Target State",
              data: targetValues,
              backgroundColor: "rgba(139, 195, 74, 0.1)",
              borderColor: "rgba(139, 195, 74, 0.5)",
              borderWidth: 2,
              borderDash: [5, 5],
              pointBackgroundColor: "rgba(139, 195, 74, 0.5)",
              pointBorderColor: "#fff",
              pointBorderWidth: 2,
              pointRadius: 4,
              pointHoverRadius: 6,
            },
          ],
        },
        options: {
          responsive: true,
          maintainAspectRatio: false,
          animation: false,
          plugins: {
            legend: {
              position: "top",
              labels: {
                font: { size: 14, weight: "bold" },
                padding: 20,
                usePointStyle: true,
                color: "#37474F",
              },
            },
            tooltip: {
              backgroundColor: "rgba(27, 94, 32, 0.9)",
              padding: 12,
              cornerRadius: 8,
              titleFont: { size: 14, weight: "bold" },
              bodyFont: { size: 13 },
            },
          },
          scales: {
            r: {
              angleLines: { display: true, color: "rgba(0, 0, 0, 0.1)" },
              suggestedMin: 0,
              suggestedMax: 5,
              ticks: {
                stepSize: 1,
                font: { size: 12 },
                backdropColor: "transparent",
                color: "#546E7A",
              },
              pointLabels: {
                font: { size: 14, weight: "bold" },
                color: "#1B5E20",
                padding: 15,
              },
              grid: {
                color: "rgba(76, 175, 80, 0.2)",
                circular: true,
              },
            },
          },
        },
      });
    } else if (chartInstanceRef.current) {
      // Update existing chart without recreating
      chartInstanceRef.current.data.labels = labels;
      chartInstanceRef.current.data.datasets[0].data = currentValues as any; // safe numeric array
      chartInstanceRef.current.data.datasets[1].data = targetValues as any;
      chartInstanceRef.current.update();
    }

    if (chartRef.current) {
      chartRef.current.setAttribute("data-chart-initialized", "true");
    }

    return () => {
      // Only destroy on unmount
      if (!chartRef.current && chartInstanceRef.current) {
        chartInstanceRef.current.destroy();
        chartInstanceRef.current = null;
      }
    };
  }, [processGroups]);

  return (
    <div className="bg-momentum-pale-green/30 rounded-xl p-8 mb-12 border border-momentum-accent/20" style={{ height: "500px" }}>
      <canvas ref={chartRef}></canvas>
    </div>
  );
}
